import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FeedbackReviewDialog } from "./FeedbackReviewDialog";
import { Loader2, MessageSquare, CheckCircle2, XCircle, Clock, Eye, Download, Trash2, Check } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import Papa from "papaparse";

type FeedbackStatus = "pending" | "under_review" | "accepted" | "rejected" | "resolved";
type FeedbackType = "incorrect_answer" | "unclear_question" | "typo" | "outdated_info" | "missing_explanation" | "other";

interface Feedback {
  id: string;
  mcq_id: string;
  user_id: string;
  feedback_type: FeedbackType;
  feedback_text: string;
  suggested_correction: string | null;
  status: FeedbackStatus;
  admin_response: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  is_paid_user: boolean;
  mcqs: {
    stem: string;
  } | null;
}

const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  incorrect_answer: "Incorrect Answer",
  unclear_question: "Unclear Question",
  typo: "Typo",
  outdated_info: "Outdated Info",
  missing_explanation: "Missing Explanation",
  other: "Other",
};

const STATUS_CONFIG = {
  pending: { icon: Clock, color: "bg-yellow-500", label: "Pending" },
  under_review: { icon: Eye, color: "bg-blue-500", label: "Under Review" },
  accepted: { icon: CheckCircle2, color: "bg-green-500", label: "Accepted" },
  rejected: { icon: XCircle, color: "bg-red-500", label: "Rejected" },
  resolved: { icon: CheckCircle2, color: "bg-primary", label: "Resolved" },
};

export function UserFeedbackManager() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [userTypeFilter, setUserTypeFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: feedbackList, isLoading, refetch } = useQuery({
    queryKey: ["user-feedback", statusFilter, typeFilter, userTypeFilter, searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("mcq_user_feedback")
        .select(`
          *,
          mcqs (stem)
        `)
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter as FeedbackStatus);
      }

      if (typeFilter !== "all") {
        query = query.eq("feedback_type", typeFilter as FeedbackType);
      }

      if (userTypeFilter !== "all") {
        query = query.eq("is_paid_user", userTypeFilter === "paid");
      }

      if (searchQuery) {
        query = query.or(`feedback_text.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Feedback[];
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ ids, updates }: { ids: string[]; updates: Partial<Feedback> }) => {
      const { error } = await supabase
        .from("mcq_user_feedback")
        .update(updates)
        .in("id", ids);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-feedback"] });
      setSelectedIds([]);
      toast.success("Bulk update completed");
      refetch();
    },
  });

  const handleReview = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setReviewDialogOpen(true);
  };

  const handleDialogClose = () => {
    setReviewDialogOpen(false);
    setSelectedFeedback(null);
    refetch();
  };

  const handleBulkMarkReviewed = () => {
    bulkUpdateMutation.mutate({
      ids: selectedIds,
      updates: { status: "resolved" as FeedbackStatus },
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && feedbackList) {
      setSelectedIds(feedbackList.map(f => f.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    }
  };

  const handleExportCSV = () => {
    if (!feedbackList) return;

    const exportData = feedbackList
      .filter(f => selectedIds.length === 0 || selectedIds.includes(f.id))
      .map(f => ({
        "MCQ Stem": f.mcqs?.stem || "N/A",
        "Feedback Type": FEEDBACK_TYPE_LABELS[f.feedback_type],
        "Feedback Text": f.feedback_text,
        "Suggested Correction": f.suggested_correction || "",
        "Status": STATUS_CONFIG[f.status].label,
        "User Type": f.is_paid_user ? "Paid" : "Free",
        "Created At": format(new Date(f.created_at), "yyyy-MM-dd HH:mm:ss"),
        "Admin Response": f.admin_response || "",
        "Reviewed At": f.reviewed_at ? format(new Date(f.reviewed_at), "yyyy-MM-dd HH:mm:ss") : "",
      }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `feedback-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  const activeFiltersCount = 
    (statusFilter !== "all" ? 1 : 0) +
    (typeFilter !== "all" ? 1 : 0) +
    (userTypeFilter !== "all" ? 1 : 0) +
    (searchQuery ? 1 : 0);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  User Feedback Queue
                  {activeFiltersCount > 0 && (
                    <Badge variant="secondary">{activeFiltersCount} filters active</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Review and respond to user-submitted MCQ improvement suggestions
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {selectedIds.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkMarkReviewed}
                    disabled={bulkUpdateMutation.isPending}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Mark {selectedIds.length} as Reviewed
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportCSV}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Input
                placeholder="Search feedback..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-xs"
              />
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Feedback Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="incorrect_answer">Incorrect Answer</SelectItem>
                  <SelectItem value="unclear_question">Unclear Question</SelectItem>
                  <SelectItem value="typo">Typo</SelectItem>
                  <SelectItem value="outdated_info">Outdated Info</SelectItem>
                  <SelectItem value="missing_explanation">Missing Explanation</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>

              <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="User Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="paid">Paid Users</SelectItem>
                  <SelectItem value="free">Free Users</SelectItem>
                </SelectContent>
              </Select>

              {activeFiltersCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setStatusFilter("all");
                    setTypeFilter("all");
                    setUserTypeFilter("all");
                    setSearchQuery("");
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !feedbackList || feedbackList.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No feedback found
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedIds.length === feedbackList.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedbackList.map((feedback) => {
                    const StatusIcon = STATUS_CONFIG[feedback.status].icon;
                    return (
                      <TableRow key={feedback.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(feedback.id)}
                            onCheckedChange={(checked) => handleSelectOne(feedback.id, !!checked)}
                          />
                        </TableCell>
                        <TableCell className="max-w-md">
                          <p className="line-clamp-2 text-sm">
                            {feedback.mcqs?.stem || "Question not found"}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {FEEDBACK_TYPE_LABELS[feedback.feedback_type]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {feedback.is_paid_user && (
                              <Badge variant="default" className="text-xs">
                                Paid
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <StatusIcon className="h-4 w-4" />
                            <span className="text-sm">{STATUS_CONFIG[feedback.status].label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(feedback.created_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReview(feedback)}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedFeedback && (
        <FeedbackReviewDialog
          open={reviewDialogOpen}
          onOpenChange={handleDialogClose}
          feedback={selectedFeedback}
        />
      )}
    </>
  );
}
